import { createBrowserRouter, generatePath, Navigate, RouterProvider, useLocation, useParams, type RouteObject } from 'react-router';
import ErrorBoundary, { RouteErrorFallback } from './components/layouts/error-boundary';
import AppLayout from './components/layouts/app-layout';
import SettingsLayout from './components/layouts/settings-layout';
import TrainingLayout from './components/layouts/training-layout';
import AgentsLayout from './components/layouts/agents-layout';
import AnalyticsLayout from './components/layouts/analytics-layout';
import TestLayout, { TestIndexRedirect } from './components/layouts/test-layout';
import SuperAdminDeveloperLayout from './components/layouts/super-admin-developer-layout';
import SuperAdminLayout from './components/layouts/super-admin-layout';
import AuthLayout from './components/layouts/auth-layout';
import GuestRoute from './components/layouts/guest-route';
import ProtectedRoute from './components/layouts/protected-route';
import RouteGuard from './components/layouts/route-guard';
import AnalyticsV2 from './routes/analytics-v2';
import Overview from './routes/overview';
import Sources from './routes/sources';
import SourceDetailRoute from './routes/source-detail';
import Playground from './routes/playground';
import Conversations from './routes/conversations';
import SentinelConversations from './routes/sentinel-conversations';
import SentinelWorkspaceRoute from './routes/sentinel-workspace';
import ClientFeedbacks from './routes/client-feedbacks';
import SocialMediaAgent from './routes/agents/social-media-agent';
import SalesAgent from './routes/agents/sales-agent';
import ActionLog from './routes/agents/action-log';
import Behaviour from './routes/settings/behaviour';
import Integrations from './routes/settings/integrations';
import IntegrationActionsPage from './routes/settings/integration-actions';
import IntegrationActionDetailPage from './routes/settings/integration-action-detail';
import CustomToolsPage from './routes/settings/custom-tools';
import CustomToolBuilderPage from './routes/settings/custom-tool-builder';
import GlobalPreconditionsPage from './routes/settings/global-preconditions';
import Guardrails from './routes/settings/guardrails';
import DeveloperSettings from './routes/settings/developer';
import BrandSettingsPage from './routes/settings/brand';
import UsersPage from './routes/settings/users';
import SentinelWorkspaceSettingsPage from './routes/settings/sentinel-workspace';
import SettingsAutomationPage from './routes/settings/automation';
import TicketProcessingPage from './routes/settings/ticket-processing';
import IngestionPage from './routes/settings/ingestion';
import HandlingPage from './routes/settings/handling';
import LogsPage from './routes/settings/logs';
import EscalationsPage from './routes/settings/escalations';
import OverflowPage from './routes/settings/overflow'; // OVERFLOW DISABLED (route commented below)
import AiPlanPage from './routes/settings/ai-plan';
import IntentsPage from './routes/settings/intents';
import Instructions from './routes/training/instructions';
import SOPDetailRoute from './routes/training/sop-detail';
import BackTesting from './routes/training/back-testing';
import PromotedWorkflows from './routes/training/promoted-workflows';
import PromotedKnowledge from './routes/training/promoted-knowledge';
import PersonaRoute from './routes/training/persona';
import ResponseStrategies from './routes/training/response-strategies';
import ResponseStrategyForm from './routes/training/response-strategy-form';
import ResponseStrategyTemplateForm from './routes/training/response-strategy-template-form';
import PersonaGuideRoute from './routes/persona-guide';
import TicketCategorization, { SourcesRedirect } from './routes/ticket-categorization';
import InternalLogin from './routes/internal-login';
import ForgotPassword from './routes/forgot-password';
import SetPassword from './routes/set-password';
import AccountsPage from './routes/super-admin/accounts';
import InternalAnalyticsDashboard from './routes/super-admin/analytics';
import CSIntentsPage from './routes/super-admin/cs-intents';
import ToolClientsPage from './routes/super-admin/tool-clients';
import ExtensionVersionsPage from './routes/super-admin/extension-versions';
import RolePermissionsPage from './routes/super-admin/role-permissions';
import FeedbacksPage from './routes/super-admin/feedbacks';
import NotFound from './routes/not-found';
import { Toaster } from './components/ui/sonner';
import { UpdateNudge } from './components/update-nudge';
import { addBreadcrumb } from '@/lib/sentry/sentry-utils';
import { ERROR_LEVELS } from '@/constants/sentry-constants';
import { BACK_TESTING_PATH, BACK_TESTING_BULK_RUN_PATH, SIMULATOR_PATH } from '@/constants/paths';
import { CUSTOM_PROVIDER, CUSTOM_TOOLS_PATH } from '@/modules/integrations/custom-tools';

/** Old paths live on in bookmarks and shared links; carry the query string through the move. */
function RedirectWithSearch({ to }: { to: string }) {
	const { search, hash } = useLocation();
	return <Navigate to={to + search + hash} replace />;
}

/** The same, for a path that carries the `:id` it was opened with. */
function RedirectWithParam({ to }: { to: string }) {
	const params = useParams();
	return <RedirectWithSearch to={generatePath(to, params)} />;
}

const routes: RouteObject[] = [
	// Auth routes (guest only — redirect to / if already authenticated)
	// Backward-compat: old login path now redirects to the user login
	{
		path: '/internal-login',
		element: <Navigate to='/login' replace />
	},
	{
		element: <GuestRoute />,
		children: [
			{
				path: '/login',
				element: <AuthLayout />,
				children: [{ index: true, element: <InternalLogin /> }]
			},
			{
				path: '/super_admin/login',
				element: <AuthLayout />,
				children: [{ index: true, element: <InternalLogin /> }]
			},
			{
				path: '/forgot-password',
				element: <AuthLayout subtitle='Forgot password' />,
				children: [{ index: true, element: <ForgotPassword /> }]
			},
			{
				path: '/set-password',
				element: <AuthLayout />,
				children: [{ index: true, element: <SetPassword /> }]
			}
		]
	},
	// Protected routes with route guard
	{
		path: '/',
		element: <ProtectedRoute />,
		children: [
			{
				element: <RouteGuard />,
				children: [
					// Regular user routes
					{
						path: '/',
						element: <AppLayout />,
						children: [
							{
								index: true,
								element: <Overview />
							},
							{
								path: 'analytics-v2',
								element: <AnalyticsV2 />,
								handle: { subject: 'analytics' }
							},
							{
								// Moved inside Training; old links and bookmarks still land.
								path: 'sources',
								children: [
									{ index: true, element: <SourcesRedirect /> },
									{ path: ':id', element: <SourcesRedirect /> }
								]
							},
							{
								path: 'training',
								element: <TrainingLayout />,
								children: [
									{
										index: true,
										element: <Navigate to='protocols' replace />
									},
									{
										// Workflows are protocols now: every old /workflows and /workflows/v2 link opens
										// the protocol it names. Any workflow type opens, and compiles, as a protocol.
										path: 'workflows',
										children: [
											{ index: true, element: <RedirectWithSearch to='/training/protocols' /> },
											{ path: 'new', element: <RedirectWithSearch to='/training/protocols/new' /> },
											{ path: 'templates/:id', element: <RedirectWithParam to='/training/protocols/templates/:id' /> },
											{
												path: 'v2',
												children: [
													{ index: true, element: <RedirectWithSearch to='/training/protocols' /> },
													{ path: 'new', element: <RedirectWithSearch to='/training/protocols/new' /> },
													// A standalone SEG has no protocol to open; the listing is the nearest place.
													{ path: 'segs/:id', element: <RedirectWithSearch to='/training/protocols' /> },
													{ path: ':id', element: <RedirectWithParam to='/training/protocols/:id' /> }
												]
											},
											{ path: ':id', element: <RedirectWithParam to='/training/protocols/:id' /> }
										]
									},
									{
										path: 'sources',
										handle: { subject: 'sources' },
										children: [
											{
												index: true,
												element: <Sources />
											},
											{
												path: ':id',
												element: <SourceDetailRoute />
											}
										]
									},
									{
										path: 'protocols',
										handle: { subject: 'workflow' },
										children: [
											{
												index: true,
												lazy: async () => {
													const { default: Protocols } = await import('./routes/training/protocols');
													return { Component: Protocols };
												}
											},
											{
												path: 'new',
												// Split like the other training routes: the editor and its TipTap
												// extensions are heavy enough to push the main chunk past the build's heap.
												lazy: async () => {
													const { default: ProtocolForm } = await import('./routes/training/protocol-form');
													return { Component: ProtocolForm };
												},
												handle: { subject: 'workflow', require: 'edit' }
											},
											{
												// A template, previewed in the protocol editor before it is used.
												path: 'templates/:id',
												lazy: async () => {
													const { default: ProtocolTemplate } = await import('./routes/training/protocol-template');
													return { Component: ProtocolTemplate };
												}
											},
											{
												path: ':id',
												lazy: async () => {
													const { default: ProtocolForm } = await import('./routes/training/protocol-form');
													// Keyed by id so moving straight to another protocol remounts the editor:
													// its draft, AI edit session and diff base must never carry across.
													function ProtocolFormById() {
														const { id } = useParams();
														return <ProtocolForm key={id} />;
													}
													return { Component: ProtocolFormById };
												}
											}
										]
									},
									{
										path: 'instructions',
										element: <Instructions />,
										handle: { subject: 'instructions' }
									},
									{
										path: 'promoted-blueprints',
										element: <Navigate to='/training/promoted-workflows' replace />
									},
									{
										path: 'promoted-workflows',
										element: <PromotedWorkflows />,
										handle: { subject: 'promoted-blueprint' }
									},
									{
										path: 'promoted-knowledge',
										element: <PromotedKnowledge />,
										handle: { subject: 'promoted-blueprint' }
									},
									{
										path: 'products',
										lazy: async () => {
											const { default: Products } = await import('./routes/training/products');
											return { Component: Products };
										},
										handle: { subject: 'products' }
									},
									{
										path: 'back-testing',
										element: <RedirectWithSearch to={BACK_TESTING_PATH} />
									},
									{
										path: 'back-testing/bulk-run',
										element: <RedirectWithSearch to={BACK_TESTING_BULK_RUN_PATH} />
									},
									{
										path: 'back-testing/bulk-run',
										lazy: async () => {
											const { default: BackTestingBulkRun } = await import('./routes/training/back-testing-bulk-run');
											return { Component: BackTestingBulkRun };
										},
										handle: { subject: 'sop' }
									},
									{
										path: 'behaviour',
										element: <Behaviour />,
										handle: { subject: 'memory.brand' }
									},
									{
										path: 'persona',
										element: <PersonaRoute />,
										handle: { subject: 'persona' }
									},
									{
										path: 'guardrails',
										element: <Guardrails />,
										handle: { subject: 'guardrails' }
									},
									{
										path: 'response-strategies',
										handle: { subject: 'response-strategy' },
										children: [
											{
												index: true,
												element: <ResponseStrategies />
											},
											{
												path: 'new',
												element: <ResponseStrategyForm />,
												handle: { subject: 'response-strategy', require: 'edit' }
											},
											{
												path: 'templates/:id',
												element: <ResponseStrategyTemplateForm />
											},
											{
												path: ':id',
												element: <ResponseStrategyForm />
											}
										]
									}
								]
							},
							{
								// The "SOPs" tab/nav entry under Training is removed (see menu.config.tsx),
								// but the Sources page's SOP tab (SourcesSOPTable) still links here to open
								// the SOP detail/approve viewer. Lives at the top level (not under
								// 'training') since there's no more Training tab to associate it with.
								path: 'sops',
								handle: { subject: 'sop' },
								children: [
									{
										path: 'new',
										element: <SOPDetailRoute />,
										handle: { subject: 'sop', require: 'edit' }
									},
									{
										path: ':id',
										element: <SOPDetailRoute />
									}
								]
							},
							{
								path: 'ticket-categorization',
								element: <TicketCategorization />,
								handle: { subject: 'guardrails' }
							},
							{
								// Sentinel Analytics: the same permission as the Sentinel reporting endpoints.
								path: 'analytics',
								element: <AnalyticsLayout />,
								handle: { subject: 'sentinel-workspace' },
								children: [
									{ index: true, element: <Navigate to='support/overview' replace /> },
									{
										path: 'support/overview',
										lazy: async () => ({ Component: (await import('./routes/analytics/support-overview')).default })
									},
									{
										path: 'support/performance',
										lazy: async () => ({ Component: (await import('./routes/analytics/performance')).default })
									},
									{
										path: 'inflow/overview',
										lazy: async () => ({ Component: (await import('./routes/analytics/inflow-overview')).default })
									},
									{
										path: 'inflow/message-metrics',
										lazy: async () => ({ Component: (await import('./routes/analytics/message-metrics')).default })
									},
									{
										path: 'inflow/ticket-metrics',
										lazy: async () => ({ Component: (await import('./routes/analytics/ticket-metrics')).default })
									}
								]
							},
							{
								path: 'agents',
								element: <AgentsLayout />,
								children: [
									{
										index: true,
										element: <Navigate to='social-media' replace />
									},
									{
										path: 'social-media',
										element: <SocialMediaAgent />,
										handle: { subject: 'integrations' }
									},
									{
										// Same permission subject as the social media agent: both are agents the
										// brand runs, configured by whoever manages its integrations.
										path: 'sales',
										element: <SalesAgent />,
										handle: { subject: 'integrations' }
									},
									{
										// The agent's audit log. Read-only: there is no create, edit or
										// delete route to add here, by design.
										path: 'action-log',
										element: <ActionLog />,
										handle: { subject: 'integrations' }
									}
								]
							},
							{
								path: 'playground',
								element: <RedirectWithSearch to={SIMULATOR_PATH} />
							},
							{
								path: 'test',
								element: <TestLayout />,
								children: [
									{
										index: true,
										element: <TestIndexRedirect />
									},
									{
										path: 'playground',
										lazy: async () => {
											const { default: PlaygroundChat } = await import('./routes/playground-chat');
											return { Component: PlaygroundChat };
										},
										handle: { subject: 'playground' }
									},
									{
										path: 'simulator',
										element: <Playground />,
										handle: { subject: 'playground' }
									},
									{
										path: 'back-testing',
										element: <BackTesting />,
										handle: { subject: 'sop' }
									},
									{
										path: 'back-testing/bulk-run',
										lazy: async () => {
											const { default: BackTestingBulkRun } = await import('./routes/training/back-testing-bulk-run');
											return { Component: BackTestingBulkRun };
										},
										handle: { subject: 'sop' }
									},
									{
										path: 'datasets',
										lazy: async () => {
											const { default: SimulationDatasets } = await import('./routes/training/simulation-datasets');
											return { Component: SimulationDatasets };
										},
										handle: { subject: 'sop' }
									},
									{
										path: 'datasets/:datasetId',
										lazy: async () => {
											const { default: SimulationDataset } = await import('./routes/training/simulation-dataset');
											return { Component: SimulationDataset };
										},
										handle: { subject: 'sop' }
									}
								]
							},
							{
								path: 'conversations',
								element: <Conversations />,
								handle: { subject: 'shadow-mode' }
							},
							{
								path: 'sentinel-conversations',
								element: <SentinelConversations />,
								handle: { subject: 'client-review' }
							},
							{
								// The AI Operating Dashboard's gap board. `:gapId` is a full-width
								// route (not a drawer over the list) so it can hold its own page
								// header and, later, a persistent right-hand testing rail.
								path: 'gap-report',
								handle: { subject: 'gap-report' },
								children: [
									{
										index: true,
										lazy: async () => {
											const { default: GapReport } = await import('./routes/gap-report');
											return { Component: GapReport };
										}
									},
									{
										path: ':gapId',
										lazy: async () => {
											const { default: GapReportDetail } = await import('./routes/gap-report-detail');
											return { Component: GapReportDetail };
										}
									}
								]
							},
							{
								path: 'sentinel-workspace',
								element: <SentinelWorkspaceRoute />,
								handle: { subject: 'sentinel-workspace' }
							},
							{
								// Own subject so the super-admin grid can grant this page
								// independently of the Review Dashboard (client-review).
								path: 'client-feedbacks',
								element: <ClientFeedbacks />,
								handle: { subject: 'client-feedback' }
							},
							{
								path: 'settings',
								element: <SettingsLayout />,
								children: [
									{
										index: true,
										element: <Navigate to='users' replace />
									},
									{
										path: 'ingestion',
										element: <IngestionPage />,
										handle: { subject: 'brand' }
									},
									{
										// Moved into Ingestion; old links land on the matching tab.
										path: 'operating-hours',
										element: <Navigate to='/settings/ingestion?tab=operating-hours' replace />
									},
									{
										path: 'escalations',
										element: <EscalationsPage />,
										handle: { subject: 'brand' }
									},
									{
										path: 'handling',
										element: <HandlingPage />,
										handle: { subject: 'brand' }
									},
									{
										// Moved into Handling; old links land on its Pre-Processing tab.
										path: 'ticket-preprocessing',
										element: <Navigate to='/settings/handling?tab=pre-processing' replace />
									},
									{
										path: 'overflow',
										element: <OverflowPage />,
										handle: { subject: 'brand' }
									},
									{
										path: 'logs',
										element: <LogsPage />,
										handle: { subject: 'automation' }
									},
									{
										path: 'users',
										element: <UsersPage />,
										handle: { subject: 'brand' }
									},
									{
										path: 'automation',
										element: <SettingsAutomationPage />,
										// View is enough to open it; the page locks its controls without `edit`.
										handle: { subject: 'automation' }
									},
									{
										path: 'intents',
										element: <IntentsPage />,
										// Same subject as Automation: intents drive automation eligibility.
										handle: { subject: 'automation' }
									},
									{
										// Readable by anyone who can see SOPs: it explains why a SOP step was
										// pulled out of a workflow. Editing is gated in the page itself, on the
										// same authority as approving a SOP.
										path: 'ticket-processing',
										element: <TicketProcessingPage />,
										handle: { subject: 'sop' }
									},
									{
										// The widget's setting now lives on Brand Settings; old links land there.
										path: 'widget',
										element: <Navigate to='/settings/brand' replace />
									},
									{
										path: 'sentinel-workspace',
										element: <SentinelWorkspaceSettingsPage />,
										handle: { subject: 'sentinel-workspace', require: 'edit', adminOnly: true }
									},
									{
										path: 'ai-plan',
										element: <AiPlanPage />,
										handle: { subject: 'brand' }
									},
									{
										// Both reply thresholds now live on Automation > Set Up; old links land there.
										path: 'confidence',
										element: <Navigate to='/settings/automation?tab=setup' replace />
									},
									{
										path: 'integrations',
										handle: { subject: 'integrations' },
										children: [
											{ index: true, element: <Integrations /> },
											// Custom tools are listed on their own page, not as one integration's actions.
											{ path: `${CUSTOM_PROVIDER}/actions`, element: <Navigate to={CUSTOM_TOOLS_PATH} replace /> },
											{ path: 'preconditions', element: <GlobalPreconditionsPage /> },
											{ path: ':provider/actions', element: <IntegrationActionsPage /> },
											{ path: ':provider/actions/:tool', element: <IntegrationActionDetailPage /> },
											{
												path: 'custom-tools',
												handle: { subject: 'integrations', require: 'edit' },
												children: [
													{ index: true, element: <CustomToolsPage /> },
													{ path: 'new', element: <CustomToolBuilderPage /> },
													{ path: ':id', element: <CustomToolBuilderPage /> }
												]
											}
										]
									},
									{
										// Moved to its own Agents section. Kept as a redirect because the old
										// path shipped and may be bookmarked or linked from elsewhere.
										path: 'social-agent',
										element: <Navigate to='/agents/social-media' replace />
									},
									{
										path: 'inbound-filters',
										element: <Navigate to='/settings/ingestion?tab=inbound' replace />
									},
									{
										// Developer mode switch — the page itself redirects non-super-admins.
										path: 'developer',
										element: <DeveloperSettings />
									},
									{
										path: 'brand',
										element: <BrandSettingsPage />,
										handle: { subject: 'brand' }
									}
								]
							}
						]
					},
					// Standalone (no AppLayout chrome) so it opens cleanly in a new tab for printing/PDF export.
					{
						path: 'persona/:id/guide',
						element: <PersonaGuideRoute />,
						handle: { subject: 'persona' }
					},
					// Super admin routes
					{
						path: '/super-admin',
						element: <SuperAdminLayout />,
						children: [
							{
								path: 'accounts',
								element: <AccountsPage />
							},
							{
								path: 'analytics',
								element: <InternalAnalyticsDashboard />
							},
							{
								path: 'cs-intents',
								element: <CSIntentsPage />
							},
							{
								path: 'tool-clients',
								element: <Navigate to='/super-admin/developers/tool-clients' replace />
							},
							{
								path: 'developers',
								element: <SuperAdminDeveloperLayout />,
								children: [
									{
										index: true,
										element: <Navigate to='tool-clients' replace />
									},
									{
										path: 'tool-clients',
										element: <ToolClientsPage />
									}
								]
							},
							{
								path: 'extension-versions',
								element: <ExtensionVersionsPage />
							},
							{
								path: 'role-permissions',
								element: <RolePermissionsPage />
							},
							{
								path: 'feedbacks',
								element: <FeedbacksPage />
							}
						]
					}
				]
			}
		]
	},
	// 404 - Catch all routes
	{
		path: '*',
		element: <NotFound />
	}
];

// One error screen for every route: without it a route that throws gets the router's bare default page.
const router = createBrowserRouter([{ errorElement: <RouteErrorFallback />, children: routes }]);

function App() {
	// Track route changes with Sentry
	addBreadcrumb({
		category: 'navigation',
		message: 'Application initialized',
		level: ERROR_LEVELS.INFO
	});

	return (
		<ErrorBoundary>
			<RouterProvider router={router} />
			<Toaster position='top-center' duration={3000} closeButton />
			<UpdateNudge />
		</ErrorBoundary>
	);
}

export default App;
